import { GroupedAttributesWithKey, SelectAttribute } from './models';
import { DataNode } from '../common';
import { packKeys, unpackKeys } from '@/app/core/utils';
import {
  AttributeLookup,
  BundleAttributesLookup,
  collectionUtils,
  AttributeLike,
} from '@ssar-app/domain';
import sortBy from 'lodash/sortBy';

const { groupBy } = collectionUtils;

export type AttributeWithGroupAndCategory = {
  group: string;
  category: string;
  code: string;
};
const unpackedKeysCache: Record<string, string[]> = {};
export class SelectAttributesManager {
  private attributeItems: SelectAttribute[];
  private checkedKeys: Set<string>;
  private middleKeys: string[];

  public getMiddleKeys(): string[] {
    return this.middleKeys;
  }

  public getCheckedKeysArray(): string[] {
    return Array.from(this.checkedKeys);
  }

  public getFinalCheckedKeysArray(): string[] {
    const selectableKeys = this.attributeItems
      .filter((a) => !a.isHidden())
      .map((a) => a.getAttributeKey());
    return this.getCheckedKeysArray().filter((a) => selectableKeys.includes(a));
  }

  public getAttributeItems(): SelectAttribute[] {
    return this.attributeItems;
  }

  public getCheckedKeysSet(): Set<string> {
    return this.checkedKeys;
  }

  constructor(
    attributesLookups: AttributeLookup[],
    providedAtts: AttributeLike[],
  ) {
    const initialCheckedKeys: Set<string> = new Set();
    const initialMiddleKeys: Set<string> = new Set();
    const attributeClassInstances: SelectAttribute[] = [];
    for (const attribute of attributesLookups || []) {
      const middleKey = packKeys()([attribute.group, attribute.category]);
      const fullKey = packKeys()([
        attribute.group,
        attribute.category,
        attribute.code,
      ]);
      const item = new SelectAttribute(
        attribute.group,
        attribute.category,
        attribute.code,
        attribute.description,
        attribute.group,
        middleKey,
        fullKey,
      );
      attributeClassInstances.push(item);
      if (
        // TODO: (CV) This if logic is being repeated multiple times. May be centalize this predicate?
        providedAtts.some(
          (a) =>
            a.code === attribute.code &&
            a.category === attribute.category &&
            a.group === attribute.group,
        )
      ) {
        initialCheckedKeys.add(item.getAttributeKey());
      }
      initialMiddleKeys.add(middleKey);
    }
    this.attributeItems = attributeClassInstances;
    this.checkedKeys = initialCheckedKeys;
    this.middleKeys = Array.from(initialMiddleKeys);
  }

  onSearchAttributes(value: string): void {
    this.attributeItems = this.attributeItems.map((attribute) => {
      attribute.setIsHiddenFilter(
        !(
          !value ||
          attribute.getCode()?.trim().toLowerCase().includes(value) ||
          attribute.getDescription()?.trim().toLowerCase().includes(value)
        ),
      );
      return attribute;
    });
  }

  onCheckAttributes(checkedNodes: DataNode[]): void {
    let checkedCodes = this.getCheckedCodes();
    for (const item of checkedNodes) {
      if (item.leafCode && !checkedCodes.includes(item.leafCode)) {
        this.checkedKeys.add(item.key);
        checkedCodes = this.getCheckedCodes();
      }
    }
  }

  onRemoveAttributes(key: string): void {
    this.checkedKeys = new Set(
      this.getCheckedKeysArray().filter((item) => {
        const unpackedItem = unpackKeys()(item);
        const unpackedKey = unpackKeys()(key);
        return unpackedItem.length === unpackedKey.length
          ? item !== key
          : !item.includes(key);
      }),
    );
  }

  onBundleChangeAttributes(attributesForBundle: string[]): void {
    for (const key of this.getCheckedKeysArray()) {
      for (const disableKey of attributesForBundle) {
        if (
          key
            .toString()
            .trim()
            .toLowerCase()
            .endsWith(disableKey.trim().toLowerCase())
        ) {
          this.checkedKeys.delete(key);
        }
      }
    }
    this.attributeItems = this.attributeItems.map((attribute) => {
      attribute.setIsHiddenBundle(
        attributesForBundle.includes(attribute.getCode() || ''),
      );
      return attribute;
    });
  }

  onFullAttributeChangeAttributes(attributesForFullAttribute: string[]): void {
    for (const key of this.getCheckedKeysArray()) {
      for (const disableKey of attributesForFullAttribute) {
        if (
          key
            .toString()
            .trim()
            .toLowerCase()
            .endsWith(disableKey.trim().toLowerCase())
        ) {
          this.checkedKeys.delete(key);
        }
      }
    }
    this.attributeItems = this.attributeItems.map((attribute) => {
      attribute.setIsHiddenFullAttribute(
        attributesForFullAttribute.includes(attribute.getCode() || ''),
      );
      return attribute;
    });
  }

  private unpackKeyInternal(key: string): string[] {
    if (!unpackedKeysCache[key]) {
      unpackedKeysCache[key] = unpackKeys()(key);
    }

    return unpackedKeysCache[key];
  }

  getCheckedUnpackedKeys(): AttributeLike[] {
    return this.getCheckedKeysArray().map((key) => {
      const [group, category, code] = this.unpackKeyInternal(key);
      return { group, category, code };
    });
  }

  getCheckedCodes(): string[] {
    return this.getCheckedKeysArray().map((key) => {
      const unpackedKeys: string[] = this.unpackKeyInternal(key);
      return unpackedKeys[unpackedKeys.length - 1];
    });
  }

  getGroupedAttributes(isSelectView: boolean): GroupedAttributesWithKey[] {
    const attributes = this.attributeItems.filter((attribute) => {
      if (isSelectView) {
        return (
          !attribute.isIsHiddenBundle() && !attribute.isIsHiddenFullAttribute()
        );
      } else {
        return this.checkedKeys.has(attribute.getAttributeKey());
      }
    });

    const groupedByGroup = groupBy(
      attributes || [],
      (a) => a.getGroup(),
      (a) => a,
    );
    const toReturn: GroupedAttributesWithKey[] = Object.keys(
      groupedByGroup,
    ).map((group) => {
      const groupedByCategories = groupBy(
        groupedByGroup[group],
        (a) => a.getCategory(),
        (a) => ({
          code: a.getCode(),
          groupKey: a.getGroupKey(),
          categoryKey: a.getCategoryKey(),
          attributeKey: a.getAttributeKey(),
          key: a.getAttributeKey(),
          description: a.getDescription(),
          isHiddenFilter: a.isIsHiddenFilter(),
        }),
      );

      const categories = Object.keys(groupedByCategories).map((category) => {
        const attributes = sortBy(
          groupedByCategories[category] || [],
          (a) => a.code,
        );
        return {
          name: category,
          key: attributes[0].categoryKey,
          attributes,
        };
      });
      return {
        name: group,
        key: categories[0].attributes[0].groupKey,
        categories: sortBy(categories || [], (a) => a.name),
      };
    });
    return sortBy(toReturn || [], (a) => a.name);
  }

  getAttributesForBundle(
    bundleNames: string[],
    bundleAttributes: BundleAttributesLookup[],
  ): string[] {
    return bundleAttributes
      .filter((a) => bundleNames.includes(a.code))
      .reduce((acc: string[], curr) => acc.concat(curr.attributeCodes), []);
  }

  getAttributesForFullAttribute(
    fullAttributeNames: string[],
    attributesFromFullAttribute: BundleAttributesLookup[],
  ): string[] {
    return attributesFromFullAttribute
      .filter((a) => fullAttributeNames.includes(a.code))
      .reduce((acc: string[], curr) => acc.concat(curr.attributeCodes), []);
  }
}
